import { useEffect, useState, useCallback } from 'react'
import { Download, Share, Plus, X } from 'lucide-react'

/**
 * InstallPrompt — non-intrusive PWA install affordance.
 *
 * Two flows:
 *   1. Chromium / Edge / Android Chrome → captures `beforeinstallprompt`,
 *      shows a slide-up card with «Установить приложение» CTA. Calling
 *      `prompt()` opens the browser-native install dialog.
 *   2. iOS Safari (no `beforeinstallprompt`) → shows the manual hint
 *      «Поделиться → На экран Домой» with the share icon, since iOS only
 *      allows install from the share sheet.
 *
 * Hiding logic:
 *   - Already installed (`display-mode: standalone` or `navigator.standalone`)
 *     → never render.
 *   - User dismissed → remember in localStorage for 14 days.
 *   - User accepted → never re-show.
 *
 * Anti-noise: appears only after 8 seconds of session activity, so it
 * doesn't interrupt login / first navigation.
 */

const DISMISS_KEY = 'onboardix_pwa_install_dismissed_until'
const ACCEPTED_KEY = 'onboardix_pwa_installed'
const DISMISS_DAYS = 14
const SHOW_DELAY_MS = 8_000

function isStandalone() {
    if (typeof window === 'undefined') return false
    if (window.matchMedia?.('(display-mode: standalone)').matches) return true
    if (window.matchMedia?.('(display-mode: window-controls-overlay)').matches) return true
    if (window.navigator.standalone === true) return true // iOS
    return false
}

function isIos() {
    if (typeof navigator === 'undefined') return false
    const ua = navigator.userAgent
    const isIosDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream
    const isIpadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
    return isIosDevice || isIpadOs
}

function isDismissed() {
    try {
        if (localStorage.getItem(ACCEPTED_KEY)) return true
        const until = Number(localStorage.getItem(DISMISS_KEY) || '0')
        return until && Date.now() < until
    } catch {
        return false
    }
}

export default function InstallPrompt() {
    const [deferredEvent, setDeferredEvent] = useState(null)
    const [showIosHint, setShowIosHint] = useState(false)
    const [visible, setVisible] = useState(false)

    // ── Capture beforeinstallprompt (Chromium) ──────────────────────
    useEffect(() => {
        if (isStandalone() || isDismissed()) return

        const onBefore = (e) => {
            e.preventDefault()
            setDeferredEvent(e)
            setTimeout(() => setVisible(true), SHOW_DELAY_MS)
        }
        const onInstalled = () => {
            try { localStorage.setItem(ACCEPTED_KEY, String(Date.now())) } catch { /* noop */ }
            setDeferredEvent(null)
            setShowIosHint(false)
            setVisible(false)
        }

        window.addEventListener('beforeinstallprompt', onBefore)
        window.addEventListener('appinstalled', onInstalled)

        // iOS: no beforeinstallprompt — schedule manual hint after delay
        if (isIos()) {
            const t = setTimeout(() => {
                if (!isStandalone() && !isDismissed()) {
                    setShowIosHint(true)
                    setVisible(true)
                }
            }, SHOW_DELAY_MS)
            return () => {
                clearTimeout(t)
                window.removeEventListener('beforeinstallprompt', onBefore)
                window.removeEventListener('appinstalled', onInstalled)
            }
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', onBefore)
            window.removeEventListener('appinstalled', onInstalled)
        }
    }, [])

    const handleInstall = useCallback(async () => {
        if (!deferredEvent) return
        try {
            deferredEvent.prompt()
            const { outcome } = await deferredEvent.userChoice
            if (outcome === 'accepted') {
                try { localStorage.setItem(ACCEPTED_KEY, String(Date.now())) } catch { /* noop */ }
            } else {
                handleDismiss()
            }
        } catch {
            // prompt() can throw if browser refuses (rate-limit, already shown)
        } finally {
            setDeferredEvent(null)
            setVisible(false)
        }
    }, [deferredEvent])

    const handleDismiss = useCallback(() => {
        try {
            const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000
            localStorage.setItem(DISMISS_KEY, String(until))
        } catch { /* noop */ }
        setVisible(false)
        setDeferredEvent(null)
        setShowIosHint(false)
    }, [])

    if (!visible) return null
    if (!deferredEvent && !showIosHint) return null

    const cardStyle = {
        position: 'fixed',
        left: '50%',
        bottom: 'max(20px, env(safe-area-inset-bottom))',
        transform: 'translateX(-50%)',
        zIndex: 99998,
        background: 'color-mix(in srgb, var(--color-shell-surface) 96%, transparent)',
        color: 'var(--color-shell-text)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        padding: '14px 18px',
        borderRadius: 'var(--radius-lg)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '0.875rem',
        maxWidth: 'min(420px, calc(100vw - 24px))',
        border: '1px solid rgba(255,255,255,0.08)',
        animation: 'onboardix-pwa-slideup 0.35s ease',
    }
    const btnPrimary = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 14px',
        borderRadius: 'var(--radius-md)',
        border: 'none',
        background: 'var(--color-accent)',
        color: 'var(--color-text-inverse)',
        fontWeight: 600,
        fontSize: '0.812rem',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    }
    const btnDismiss = {
        background: 'transparent',
        border: 'none',
        color: 'var(--color-text-muted)',
        cursor: 'pointer',
        padding: 4,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'var(--radius-sm)',
    }

    return (
        <>
            <style>{`
                @keyframes onboardix-pwa-slideup {
                    from { transform: translate(-50%, 16px); opacity: 0 }
                    to   { transform: translate(-50%, 0);    opacity: 1 }
                }
            `}</style>
            <div role="dialog" aria-label="Установить приложение" style={cardStyle}>
                {showIosHint ? (
                    <>
                        <Share size={22} color='var(--color-accent)' style={{ flexShrink: 0 }} />
                        <div style={{ flex: 1, lineHeight: 1.4 }}>
                            Установите Onboardix:&nbsp;нажмите{' '}
                            <Share size={14} style={{ verticalAlign: '-2px' }} /> и
                            выберите «На экран Домой» <Plus size={14} style={{ verticalAlign: '-2px' }} />.
                        </div>
                    </>
                ) : (
                    <>
                        <Download size={22} color='var(--color-accent)' style={{ flexShrink: 0 }} />
                        <div style={{ flex: 1, lineHeight: 1.4 }}>
                            Установите Onboardix как приложение — быстрее запуск, оффлайн-доступ.
                        </div>
                        <button type="button" style={btnPrimary} onClick={handleInstall}>
                            Установить
                        </button>
                    </>
                )}
                <button
                    type="button"
                    style={btnDismiss}
                    onClick={handleDismiss}
                    aria-label="Закрыть"
                    title="Не показывать 14 дней"
                >
                    <X size={18} />
                </button>
            </div>
        </>
    )
}
